/**
 * global.js
 * Shared utilities: screen reader announcements, accessibility settings
 * persistence, and focus trap management.
 */

// ---------------------------------------------------------------------------
// Screen reader announcer
// ---------------------------------------------------------------------------

/**
 * Announce a message to screen readers via the persistent live region.
 * Clears the element first so repeated identical messages still fire.
 * @param {string} message
 */
function announceToScreenReader(message) {
  const el = document.getElementById('aria-announcer');
  if (!el) return;
  el.textContent = '';
  requestAnimationFrame(() => {
    el.textContent = message;
  });
}

// ---------------------------------------------------------------------------
// Keyboard focus modality
// ---------------------------------------------------------------------------

// Tracks whether the user is navigating by keyboard, so base.css can hide the focus ring on
// form fields that were clicked into (browsers always treat text fields as :focus-visible).
document.addEventListener(
  'keydown',
  (event) => {
    if (event.key === 'Tab') document.documentElement.classList.add('is-tabbing');
  },
  true
);
document.addEventListener(
  'pointerdown',
  () => {
    document.documentElement.classList.remove('is-tabbing');
  },
  true
);

// ---------------------------------------------------------------------------
// Focus trap
// ---------------------------------------------------------------------------

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[contenteditable]',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

let _trapElement = null;
let _trapHandler = null;

/**
 * Trap Tab / Shift+Tab focus within the given element.
 * Call releaseFocusTrap() to remove the listener when the element closes.
 * @param {HTMLElement} element
 */
function focusTrap(element) {
  releaseFocusTrap();

  _trapElement = element;

  _trapHandler = function (event) {
    if (event.key !== 'Tab') return;

    const focusable = Array.from(
      _trapElement.querySelectorAll(FOCUSABLE_SELECTORS)
    ).filter((el) => !el.closest('[hidden]') && el.offsetParent !== null);

    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey) {
      if (document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  };

  document.addEventListener('keydown', _trapHandler);
}

/**
 * Remove the active focus trap listener.
 */
function releaseFocusTrap() {
  if (_trapHandler) {
    document.removeEventListener('keydown', _trapHandler);
  }
  _trapElement = null;
  _trapHandler = null;
}

// ---------------------------------------------------------------------------
// Page scroll lock (drawers, lightbox, modals with inner scroll regions)
// ---------------------------------------------------------------------------

let _scrollLockCount = 0;
let _scrollLockY = 0;

/**
 * Lock document scroll while an overlay is open. Reference-counted so multiple
 * surfaces (e.g. cart + backdrop) can lock without unlocking prematurely.
 */
function lockPageScroll() {
  if (_scrollLockCount === 0) {
    _scrollLockY = window.scrollY;
    document.documentElement.classList.add('is-scroll-locked');
    document.body.classList.add('is-scroll-locked');
    document.body.style.setProperty('--scroll-lock-top', `-${_scrollLockY}px`);
  }
  _scrollLockCount += 1;
}

/**
 * Move focus without scrolling the document (e.g. fixed header triggers on close).
 * @param {HTMLElement|null} element
 */
function focusWithoutScroll(element) {
  if (!element || typeof element.focus !== 'function') return;
  try {
    element.focus({ preventScroll: true });
  } catch (_) {
    element.focus();
  }
}

/**
 * Release one scroll lock. Restores page scroll when the last lock closes.
 */
function unlockPageScroll() {
  if (_scrollLockCount === 0) return;
  _scrollLockCount -= 1;
  if (_scrollLockCount > 0) return;

  const y = _scrollLockY;
  const root = document.documentElement;
  const prevScrollBehavior = root.style.scrollBehavior;

  root.style.scrollBehavior = 'auto';
  root.classList.remove('is-scroll-locked');
  document.body.classList.remove('is-scroll-locked');
  document.body.style.removeProperty('--scroll-lock-top');
  window.scrollTo(0, y);

  requestAnimationFrame(() => {
    if (window.scrollY !== y) {
      window.scrollTo(0, y);
    }
    root.style.scrollBehavior = prevScrollBehavior;
  });
}

// ---------------------------------------------------------------------------
// Accessibility settings — persist to / restore from localStorage
// ---------------------------------------------------------------------------

const A11Y_STORAGE_KEY = 'hattie-a11y';

/**
 * True when in-page reduce-motion is on or the OS requests reduced motion.
 * @returns {boolean}
 */
function prefersReducedMotion() {
  return (
    document.documentElement.classList.contains('reduce-motion') ||
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

// ---------------------------------------------------------------------------
// Backdrop overlay (snippet-overlay.liquid)
// ---------------------------------------------------------------------------

const OVERLAY_TRANSITION_FALLBACK_MS = 150;
const MODAL_TRANSITION_FALLBACK_MS = 450;

/**
 * Fade/blur the shared backdrop in. Expects .overlay with .is-open driven styles.
 * @param {HTMLElement|null} overlay
 */
function openOverlay(overlay) {
  if (!overlay) return;

  overlay.hidden = false;

  if (prefersReducedMotion()) {
    overlay.classList.add('is-open');
    return;
  }

  overlay.classList.remove('is-open');
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      overlay.classList.add('is-open');
    });
  });
}

/**
 * Fade/blur the backdrop out, then run callback (e.g. set hidden).
 * @param {HTMLElement|null} overlay
 * @param {() => void} [callback]
 */
function animateOverlayClose(overlay, callback) {
  if (!overlay) {
    if (callback) callback();
    return;
  }

  if (prefersReducedMotion()) {
    overlay.classList.remove('is-open');
    if (callback) callback();
    return;
  }

  overlay.classList.remove('is-open');

  let finished = false;
  const complete = () => {
    if (finished) return;
    finished = true;
    if (callback) callback();
  };

  const onTransitionEnd = (event) => {
    if (event.target === overlay && event.propertyName === 'opacity') {
      complete();
    }
  };

  overlay.addEventListener('transitionend', onTransitionEnd);
  window.setTimeout(() => {
    overlay.removeEventListener('transitionend', onTransitionEnd);
    complete();
  }, OVERLAY_TRANSITION_FALLBACK_MS);
}

/**
 * Backdrop for centred dialogs (accessibility + AoC modals).
 * @returns {HTMLElement|null}
 */
function getModalOverlay() {
  return document.querySelector('[data-overlay="modal"]');
}

function openModalBackdrop() {
  document.body.classList.add('has-overlay');
  openOverlay(getModalOverlay());
}

function hideModalBackdrop() {
  document.body.classList.remove('has-overlay');
  const overlay = getModalOverlay();
  if (!overlay) return;
  overlay.hidden = true;
  overlay.classList.remove('is-open');
}

/**
 * Read saved accessibility preferences from localStorage and apply them
 * to the document on page load.
 *
 * Stored shape:
 * {
 *   fontSize:      string  — percentage value, e.g. "90", "100", "120"
 *   gridBig:       boolean — true activates the 3-column grid layout
 *   reduceMotion:  boolean — true suppresses all animations
 *   primaryColour: string  — CSS colour value, e.g. "var(--primary-colour-2)"
 * }
 */
function applyA11ySettings() {
  let prefs;

  try {
    const raw = localStorage.getItem(A11Y_STORAGE_KEY);
    prefs = raw ? JSON.parse(raw) : {};
  } catch (_) {
    prefs = {};
  }

  const html = document.documentElement;

  // Font size
  if (prefs.fontSize) {
    html.style.fontSize = prefs.fontSize + '%';
  }

  // Grid size — 'grid-big' switches the product grid to 3 columns
  if (prefs.gridBig) {
    html.classList.add('grid-big');
  }

  // Reduce motion — off by default; only on when explicitly saved as true
  if (prefs.reduceMotion === true) {
    html.classList.add('reduce-motion');
  }

  // Primary colour — overrides the CSS custom property injected in theme.liquid
  if (prefs.primaryColour) {
    html.style.setProperty('--primary-colour', prefs.primaryColour);
  }
}

document.addEventListener('DOMContentLoaded', applyA11ySettings);
